import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Droplet, Circle, Timer, Camera, Upload, ArrowRight, SkipForward, CheckCircle, FileImage } from 'lucide-react';
import Layout from '../components/Layout';
import { Btn, Alert, PageHeader, Section } from '../components/UI';
import { usePatient } from '../contexts/PatientContext';
import { API_URL, API_KEY } from '../config';

const STEPS = [
  { id: 1, icon: Droplet, title: 'Dip Strip', desc: 'Immerse the test strip in urine for approximately 10 seconds. Ensure full saturation of all reagent pads.' },
  { id: 2, icon: Circle, title: 'Dry Strip', desc: 'Remove the strip and blot gently on absorbent paper. Place strip on the color chart — Urinox-10 on left, URS-14 on right.' },
  { id: 3, icon: Timer, title: 'Wait', desc: 'Allow the required reaction time. Do not move or disturb the strip during this period.' },
  { id: 4, icon: Camera, title: 'Capture', desc: 'Take a clear, well-lit photo of the strip against the color chart. Ensure no shadows or glare.' },
];

// ─── Beautiful Circular Timer ─────────────────────────────────────────────────
function CircularTimer({ total, remaining, running }) {
  const size = 200;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? remaining / total : 0;
  const dashOffset = circumference * (1 - progress);

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  const timeStr = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  // Color transitions: blue → teal → amber → red
  const getColor = () => {
    if (progress > 0.6) return { track: '#1E6FFF', glow: 'rgba(30,111,255,0.35)' };
    if (progress > 0.3) return { track: '#00C2A8', glow: 'rgba(0,194,168,0.35)' };
    if (progress > 0.1) return { track: '#F59E0B', glow: 'rgba(245,158,11,0.35)' };
    return { track: '#EF4444', glow: 'rgba(239,68,68,0.35)' };
  };

  const { track, glow } = getColor();
  const pct = Math.round(progress * 100);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '20px 0', gap: 12,
    }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        {/* Glow backdrop */}
        <div style={{
          position: 'absolute',
          top: strokeWidth / 2, left: strokeWidth / 2,
          width: size - strokeWidth, height: size - strokeWidth,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${glow} 0%, transparent 70%)`,
          transition: 'background 1s ease',
          pointerEvents: 'none',
        }} />

        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
          {/* Background track */}
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none"
            stroke="rgba(0,0,0,0.06)"
            strokeWidth={strokeWidth}
          />
          {/* Tick marks */}
          {Array.from({ length: 60 }).map((_, i) => {
            const angle = (i / 60) * 2 * Math.PI - Math.PI / 2;
            const isMajor = i % 5 === 0;
            const innerR = radius - (isMajor ? 14 : 8);
            const outerR = radius - 2;
            return (
              <line
                key={i}
                x1={size / 2 + Math.cos(angle) * innerR}
                y1={size / 2 + Math.sin(angle) * innerR}
                x2={size / 2 + Math.cos(angle) * outerR}
                y2={size / 2 + Math.sin(angle) * outerR}
                stroke={isMajor ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.07)'}
                strokeWidth={isMajor ? 2 : 1}
                strokeLinecap="round"
              />
            );
          })}
          {/* Progress arc */}
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none"
            stroke={track}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.9s linear, stroke 1s ease' }}
          />
        </svg>

        {/* Center content */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 2,
        }}>
          <span style={{
            fontSize: 42, fontWeight: 700, color: 'var(--navy)',
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: '-1px', lineHeight: 1,
            transition: 'color 1s ease',
          }}>
            {timeStr}
          </span>
          <span style={{ fontSize: 12, color: track, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', transition: 'color 1s ease' }}>
            {running ? (remaining === 0 ? 'Done!' : 'Remaining') : 'Paused'}
          </span>
          <span style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>{pct}%</span>
        </div>
      </div>

      {/* Pulse ring when running */}
      {running && remaining > 0 && (
        <div style={{ fontSize: 12, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
            background: track, animation: 'timerPulse 1.2s ease-in-out infinite',
          }} />
          Do not disturb the strip
          <style>{`
            @keyframes timerPulse {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.4; transform: scale(0.7); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}

export default function NewAnalysisPage() {
  const navigate = useNavigate();
  const { patient } = usePatient();
  const [step, setStep] = useState(1);
  const [duration, setDuration] = useState(60);
  const [timer, setTimer] = useState(60);
  const [running, setRunning] = useState(false);
  const [canCapture, setCanCapture] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!running || timer <= 0) return;
    const id = setInterval(() => setTimer(t => t - 1), 1000);
    return () => clearInterval(id);
  }, [running, timer]);

  useEffect(() => {
    if (timer === 0 && running) { setRunning(false); setCanCapture(true); setStep(4); }
  }, [timer, running]);

  const handleNext = () => {
    if (step === 3) {
      if (!running) { setTimer(duration); setRunning(true); }
      else if (window.confirm('Skip the remaining wait time?')) { setRunning(false); setTimer(0); setCanCapture(true); setStep(4); }
    } else if (step < 4) setStep(s => s + 1);
  };

  const uploadImage = async (base64) => {
    if (!patient?.patient_id || !patient?.full_name) { setError('No patient selected. Please register a patient first.'); return; }
    setLoading(true); setError('');
    try {
      const payload = {
        AnalysisID: `analysis-${Date.now()}`,
        ExpertName: 'WebUser',
        Images: [{ ImageID: `img-${Date.now()}`, ImageBase64: base64 }],
        patient_id: patient.patient_id,
        full_name: patient.full_name,
        age: patient.age,
        phone: patient.phone,
      };
      const res = await fetch(`${API_URL}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY, 'X-API-HOST': 'web-app' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const result = await res.json();
      navigate('/result', { state: { result } });
    } catch (err) {
      setError('Analysis failed. Please ensure the image is clear and try again.');
    } finally { setLoading(false); }
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) { setError('Please select an image file.'); return; }
    const base64 = await fileToBase64(file);
    await uploadImage(base64);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const progress = ((step - 1) / 3) * 100;

  return (
    <Layout>
      <style>{`
        @media (max-width: 600px) {
          .steps-grid { grid-template-columns: 1fr !important; }
          .step-progress-labels { display: none !important; }
          .page-actions-row { flex-wrap: wrap; gap: 8px !important; }
          .page-actions-row > * { flex: 1 1 auto; min-width: 120px; }
        }
      `}</style>

      <PageHeader
        title="Urinalysis Instructions"
        subtitle={patient ? `Patient: ${patient.full_name} (${patient.patient_id})` : 'No patient selected'}
        actions={!patient && <Btn variant="outline" size="sm" onClick={() => navigate('/patient')}>+ Register Patient</Btn>}
      />

      {!patient && (
        <div style={{ marginBottom: 20 }}>
          <Alert type="warning">Please register or select a patient before running analysis.</Alert>
        </div>
      )}

      {error && <div style={{ marginBottom: 20 }}><Alert type="error">{error}</Alert></div>}

      {/* Progress stepper */}
      <div style={{ marginBottom: 28 }}>
        <div className="step-progress-labels" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          {STEPS.map(s => (
            <div key={s.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: step > s.id ? 'var(--teal)' : step === s.id ? 'var(--blue)' : 'var(--gray-200)',
                color: step >= s.id ? '#fff' : 'var(--gray-400)',
                transition: 'all 0.3s', fontWeight: 700, fontSize: 14,
              }}>
                {step > s.id ? <CheckCircle size={18} /> : s.id}
              </div>
              <span style={{ fontSize: 11, marginTop: 6, color: step >= s.id ? 'var(--blue)' : 'var(--gray-400)', fontWeight: step === s.id ? 700 : 500 }}>{s.title}</span>
            </div>
          ))}
        </div>

        {/* Mobile compact stepper */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: step > s.id ? 'var(--teal)' : step === s.id ? 'var(--blue)' : 'var(--gray-200)',
                color: step >= s.id ? '#fff' : 'var(--gray-400)',
                transition: 'all 0.3s', fontWeight: 700, fontSize: 13, flexShrink: 0,
              }}>
                {step > s.id ? <CheckCircle size={14} /> : s.id}
              </div>
              {i < STEPS.length - 1 && (
                <div style={{ flex: 1, height: 3, background: step > s.id ? 'var(--teal)' : 'var(--gray-200)', borderRadius: 2, transition: 'background 0.3s' }} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div style={{ height: 4, background: 'var(--gray-200)', borderRadius: 2, marginTop: 4 }}>
          <div style={{ height: '100%', background: 'linear-gradient(90deg, var(--blue), var(--teal))', borderRadius: 2, width: `${progress}%`, transition: 'width 0.4s ease' }} />
        </div>
      </div>

      {/* Step cards — responsive grid */}
      <div className="steps-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        {STEPS.map(s => {
          const Icon = s.icon;
          const active = step === s.id;
          const done = step > s.id;
          return (
            <div key={s.id} style={{
              background: '#fff', borderRadius: 16, padding: '20px 20px',
              border: `2px solid ${active ? 'var(--blue)' : done ? 'var(--teal)' : 'var(--gray-200)'}`,
              boxShadow: active ? '0 4px 20px rgba(30,111,255,0.12)' : 'var(--shadow-sm)',
              opacity: step < s.id ? 0.55 : 1,
              transition: 'all 0.3s',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  background: active ? 'var(--blue)' : done ? 'var(--teal)' : 'var(--gray-100)',
                }}>
                  <Icon size={19} color={active || done ? '#fff' : 'var(--gray-400)'} />
                </div>
                <div>
                  <p style={{ fontSize: 10, fontWeight: 600, color: active ? 'var(--blue)' : 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Step {s.id}</p>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)', lineHeight: 1.2 }}>{s.title}</h3>
                </div>
              </div>
              <p style={{ fontSize: 13, color: 'var(--gray-500)', lineHeight: 1.6 }}>{s.desc}</p>

              {/* ── Beautiful Circle Timer for Step 3 ── */}
              {s.id === 3 && active && (
                <div style={{ marginTop: 14 }}>
                  {!running ? (
                    <div>
                      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-700)', marginBottom: 10 }}>Select wait duration:</p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {[60, 90, 120].map(v => (
                          <button key={v} onClick={() => setDuration(v)} style={{
                            padding: '8px 16px', borderRadius: 8, border: `2px solid ${duration === v ? 'var(--blue)' : 'var(--gray-200)'}`,
                            background: duration === v ? 'var(--blue)' : '#fff', color: duration === v ? '#fff' : 'var(--gray-700)',
                            fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all 0.2s', fontFamily: "'DM Sans', sans-serif",
                          }}>{v}s</button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <CircularTimer total={duration} remaining={timer} running={running} />
                  )}
                </div>
              )}

              {/* Done state circle timer */}
              {s.id === 3 && done && canCapture && (
                <div style={{ marginTop: 12, textAlign: 'center' }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px',
                    background: 'var(--teal-soft)', borderRadius: 10, border: '1.5px solid var(--teal)',
                  }}>
                    <CheckCircle size={16} color="var(--teal)" />
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--teal)' }}>Timer Complete</span>
                  </div>
                </div>
              )}

              {/* Upload area */}
              {s.id === 4 && active && canCapture && (
                <div style={{ marginTop: 14 }}>
                  <div
                    style={{
                      border: `2px dashed ${dragOver ? 'var(--blue)' : 'var(--gray-300)'}`,
                      borderRadius: 12, padding: '24px 16px', textAlign: 'center', cursor: 'pointer',
                      background: dragOver ? 'var(--blue-pale)' : 'var(--gray-50)', transition: 'all 0.2s',
                    }}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('file-input').click()}
                  >
                    <FileImage size={26} color="var(--gray-400)" style={{ margin: '0 auto 10px' }} />
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-700)' }}>Tap to select image</p>
                    <p style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>JPG, PNG, WEBP supported</p>
                    <input
                      id="file-input" type="file" accept="image/*"
                      capture="environment"
                      style={{ display: 'none' }}
                      onChange={e => e.target.files[0] && handleFile(e.target.files[0])}
                    />
                  </div>

                  {/* Camera shortcut for mobile */}
                  <button
                    onClick={() => {
                      const inp = document.createElement('input');
                      inp.type = 'file'; inp.accept = 'image/*'; inp.capture = 'environment';
                      inp.onchange = e => e.target.files[0] && handleFile(e.target.files[0]);
                      inp.click();
                    }}
                    style={{
                      marginTop: 10, width: '100%', padding: '12px', borderRadius: 10,
                      border: '2px solid var(--blue)', background: 'var(--blue-pale)',
                      color: 'var(--blue)', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    <Camera size={18} /> Open Camera
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Action buttons */}
      <div className="page-actions-row" style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        {step < 4 && (
          <Btn
            onClick={handleNext}
            disabled={step === 4 && !canCapture}
            loading={loading}
            style={{ minWidth: 160 }}
          >
            {step === 3
              ? (running
                  ? <><SkipForward size={15} style={{ marginRight: 6 }} />Skip Timer</>
                  : <><Timer size={15} style={{ marginRight: 6 }} />Start Timer ({duration}s)</>
                )
              : <>Next Step <ArrowRight size={15} style={{ marginLeft: 6 }} /></>
            }
          </Btn>
        )}
      </div>

      {/* Loading overlay */}
      {loading && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.75)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '0 24px' }}>
          <div style={{ width: 64, height: 64, borderRadius: 20, background: 'linear-gradient(135deg, var(--blue), var(--teal))', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'pulse 1.5s infinite' }}>
            <Upload size={28} color="#fff" />
          </div>
          <p style={{ color: '#fff', fontSize: 18, fontWeight: 600, textAlign: 'center' }}>Analyzing Test Strip…</p>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, textAlign: 'center' }}>This may take a few seconds</p>
          <style>{`@keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.08)} }`}</style>
        </div>
      )}
    </Layout>
  );
}
